import React, { useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Button, Typography, Grid } from "antd";
import { hidePanel } from "@/pages/shared/shared.reducer";
import panels from "@/constants/panels";
import CustomAlert from "@/common/custom-alert";
import FloatInput from "@/common/float-input";
import { useFormContext } from "@/common/form-context";
import get from "lodash.get";
import { useUnderChangeCommentMutation } from "@/pages/dashboard/dashboard.reducer";
import { openErrorNotification } from "@/notification";

const { Paragraph } = Typography;

const UnderChangeCommentComponent = ({ selectedItems = [], refetch }) => {
  const dispatch = useDispatch();
  const visible = useSelector((state) =>
    get(state.shared, `${panels.addChangeComment}.visible`, false)
  );
  const [underChangeComment] = useUnderChangeCommentMutation();
  const { form, onFinish, markFieldAsTouched } = useFormContext();

  const screens = Grid.useBreakpoint();
  const isMobile = screens.xs;
  const comment = Form.useWatch("comment", form) || "";

  const exitPanel = () => {
    dispatch(hidePanel("addChangeCommentPanel"));
  };

  const warnVisible = useMemo(
    () => selectedItems.some(
      (item) =>
        item.underChangeComment != null && item.underChangeComment.trim() !== ""
    ),
    [selectedItems]
  );

  useEffect(() => {
    if (!visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const handleChange = useCallback(
    (e) => {
      markFieldAsTouched(e.target.id);
    },
    [markFieldAsTouched]
  );

  const handleSubmit = useCallback(
    (values) => {
      const { comment } = values;
      if (selectedItems && selectedItems.length > 0) {
        underChangeComment({
          comment,
          documents: selectedItems,
          panelId: "addChangeCommentPanel",
          refetch,
        });
      } else {
        const errorMessage =
          !selectedItems || selectedItems.length === 0
            ? "No selected documents"
            : "Invalid fields";
        openErrorNotification(errorMessage);
      }
    },
    [selectedItems, underChangeComment, refetch]
  );

  const renderItems = useMemo(
    () =>
      selectedItems.map((item, index) => (
        <Paragraph
          key={index}
          ellipsis={{ rows: 5, expandable: true, symbol: "more" }}
        >
          <div>
            <span className="body-large-roman" style={{ color: "var(--dms-color-text-muted)" }}>
              ●&nbsp;
            </span>
            <span style={{ color: "var(--dms-color-text)" }}>{item.documentNumber}</span>
          </div>
        </Paragraph>
      )),
    [selectedItems]
  );

  return (
    <>
      <Paragraph>
        * Add a comment for all to see. This can help let others know of
        problems with documents. Comments can be removed if a mistake was made.
        Change comments are removed when a newer revision is uploaded to the
        DMS.
      </Paragraph>

      {warnVisible && (
        <CustomAlert
          type="warning"
          message="One or more documents selected have a change comment already"
          visible={true}
        />
      )}
      <Form form={form} onFinish={onFinish(handleSubmit)}>
        <Form.Item
          name="comment"
          rules={[
            {
              required: true,
              message:
                "Documents cannot be placed under change without a comment.",
            },
            {
              whitespace: true,
              message:
                "Documents cannot be placed under change without a comment.",
            },
          ]}
        >
          <FloatInput
            multiline
            label="Comment"
            id="comment"
            showCount
            maxLength={1000}
            onChange={handleChange}
            autoSize={{ minRows: 3 }}
          />
        </Form.Item>
        <div className="body-large-roman mt-3" style={{ color: "var(--dms-color-text-muted)" }}>
          Document / drawing #:
        </div>
        <div style={{ gap: 6, marginBottom: 26 }}>{renderItems}</div>
      </Form>
      <div className="footer document-detail-footer"
        style={ isMobile ? {
          display: "grid",
          rowGap: "10px",
          width: "100%",
          minHeight: "22vh",
          alignContent: "flex-start"
        } : {
            display: "flex",
            paddingRight: "45px"
        }}>
        <Button
          onClick={() => form.submit()}
          type="primary"
          disabled={!comment}
          className="display-center"
        >
          <i className="ri-chat-new-line"></i>
          Add Comment(s)
        </Button>
        <Button className="common-button" onClick={exitPanel}>
          <i className="ri-close-line" />
          Cancel
        </Button>
      </div>
    </>
  );
};

export default UnderChangeCommentComponent;
